import React, { useState, useEffect, useMemo } from 'react';
import { formatVehicleBrandModel } from '../../utils/vehicleBrand';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Heading,
  Divider,
  SimpleGrid,
  IconButton,
  Textarea,
  useColorModeValue,
  List,
  ListItem,
  InputGroup,
  InputRightElement,
  Text,
  Select,
  useToast,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon, DeleteIcon } from '@chakra-ui/icons';
import { getData } from '../../utils/httpClient.js';
import api from '../../services/api.js';
import AddCustomerModal from './AddCustomerModal.js';
import { formatNumber } from '../../utils/transform.js';
import { saveInvoiceTemplate } from '../../utils/invoiceTemplates.js';
import useBcuUsdRate from '../../hooks/useBcuUsdRate.js';
import {
  formatMoneyDual,
  isUsdCurrency,
  toUyuAmount,
} from '../../utils/currencyDisplay.js';
import {
  buildRoundingLineItem,
  sumItemsTotal,
  parseDecimalValue as parseDec,
} from '../../utils/invoiceCalculations.js';
import {
  buildInvoiceLinesFromVehicleService,
  summarizeVehicleServiceForInvoice,
} from '../../utils/invoiceVehicleServiceLines.js';
import {
  INVOICE_TYPE_E_FACTURA,
  INVOICE_TYPE_OPTIONS,
  DOC_TYPE_OPTIONS,
  getDefaultDocTypeForInvoiceType,
  getInvoiceTypeLabel,
  validateInvoiceClient,
} from '../../utils/invoiceTypes.js';
import { buildInvoiceAdendaText, INVOICE_BANK_ADENDA } from '../../utils/invoiceAdenda.js';

export default function CreateInvoice({ onSubmit, products = [] }) {
  // Función para obtener la fecha de hoy en formato YYYY-MM-DD
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [form, setForm] = useState({
    tipo_comprobante: INVOICE_TYPE_E_FACTURA,
    moneda: 'UYU',
    tipo_cambio: 1,
    fecha_comprobante: getTodayDate(), // Fecha predeterminada de HOY
    due_date: '',
    forma_pago: 1,
    numero_orden_compra: '',
    descuentos_recargos: [],
    cliente: {
      tipo_doc: 2,
      cod_pais_doc: 'UY',
      nro_doc: '',
      denominacion: '',
      direccion: '',
      ciudad: '',
      departamento: '',
      pais: 'URUGUAY',
    },
    items: [],
    adenda: { texto: '' },
  });

  const [customerSearch, setCustomerSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [customerDropdown, setCustomerDropdown] = useState(false);
  const [productDropdown, setProductDropdown] = useState(false);
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const [hasSearchedCustomers, setHasSearchedCustomers] = useState(false);
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [isSearchingServices, setIsSearchingServices] = useState(false);
  const [hasSearchedServices, setHasSearchedServices] = useState(false);
  const [hasSearchedProducts, setHasSearchedProducts] = useState(false);
  const [showManualProductForm, setShowManualProductForm] = useState(false);
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const [generatingPreviewPdf, setGeneratingPreviewPdf] = useState(false);

  const toast = useToast();
  const {
    rate: bcuRate,
    info: exchangeRateInfo,
    loading: loadingExchangeRate,
  } = useBcuUsdRate(
    form.moneda === 'USD' ? form.fecha_comprobante || getTodayDate() : undefined,
  );

  useEffect(() => {
    if (form.forma_pago === 1) {
      setForm((prev) => ({
        ...prev,
        due_date: prev.fecha_comprobante || getTodayDate(),
      }));
    }
  }, [form.forma_pago, form.fecha_comprobante]);

  useEffect(() => {
    if (form.moneda !== 'USD') {
      setForm((prev) => ({ ...prev, tipo_cambio: 1 }));
      return;
    }
    if (bcuRate) {
      setForm((prev) => ({ ...prev, tipo_cambio: bcuRate }));
    }
  }, [form.moneda, bcuRate]);

  useEffect(() => {
    const draft = sessionStorage.getItem('invoice_template_draft');
    if (!draft) return;
    try {
      const tpl = JSON.parse(draft);
      setForm((prev) => ({
        ...prev,
        ...tpl,
        cliente: { ...prev.cliente, ...(tpl.cliente || {}) },
        adenda: { ...prev.adenda, ...(tpl.adenda || {}) },
        items: tpl.items || prev.items,
      }));
      sessionStorage.removeItem('invoice_template_draft');
      toast({ title: 'Plantilla aplicada', status: 'info', duration: 2500, isClosable: true });
    } catch {
      sessionStorage.removeItem('invoice_template_draft');
    }
  }, [toast]);

  // Mapeo de unidades: valor interno -> texto para mostrar
  const unitDisplayMap = {
    'un': 'UNIDAD',
    'kg': 'KILO',
    'lt': 'LITRO',
    'm': 'METRO',
    'h': 'HORA',
    'min': 'MINUTO'
  };

  // Mapeo inverso: texto para mostrar -> valor interno
  const unitValueMap = {
    'UNIDAD': 'un',
    'KILO': 'kg',
    'LITRO': 'lt',
    'METRO': 'm',
    'HORA': 'h',
    'MINUTO': 'min'
  };

  // Función para obtener el texto de visualización de una unidad
  const getUnitDisplay = (unitValue) => {
    return unitDisplayMap[unitValue] || unitValue;
  };

  // Función para obtener el valor interno de una unidad
  const getUnitValue = (unitDisplay) => {
    if (!unitDisplay) return 'un';
    return unitValueMap[unitDisplay.toUpperCase()] || unitDisplay;
  };

  // Función auxiliar para calcular productos extra
  const applyExchangeToLine = (line, rate) => {
    const productCurrency = line.currencyCode || 'UYU';
    let unitPrice = Number(line.precio) || 0;
    if (form.moneda === 'UYU' && isUsdCurrency(productCurrency)) {
      unitPrice = toUyuAmount(unitPrice, productCurrency, rate) ?? unitPrice;
    } else if (
      form.moneda === 'USD' &&
      !isUsdCurrency(productCurrency) &&
      rate > 0
    ) {
      unitPrice = unitPrice / rate;
    }
    return {
      ...line,
      precio: unitPrice,
      precio_input: String(unitPrice),
    };
  };

  // Función para búsqueda de clientes en el backend
  const searchCustomersInBackend = async (query) => {
    if (!query.trim()) {
      setFilteredCustomers([]);
      setCustomerDropdown(false);
      setHasSearchedCustomers(false);
      return;
    }

    try {
      setIsSearchingCustomers(true);
      setHasSearchedCustomers(true);
      // Endpoint corregido basado en la estructura encontrada
      const response = await getData(
        `/invoices/customers?name=${encodeURIComponent(query)}`,
      );

      const customers = response?.payload?.customers || [];

      // Debug: mostrar qué clientes están llegando
      console.log('Búsqueda de cliente:', query);
      console.log('Clientes encontrados:', customers.map(c => ({
        id: c.id,
        name: c.name,
        document: c.document,
        documentNumber: c.documentNumber,
        nroDoc: c.nro_doc,
        nroDocumento: c.nro_documento,
        allFields: c
      })));

      setFilteredCustomers(customers);
      setCustomerDropdown(true);
    } catch (error) {
      toast({
        title: 'Error al buscar clientes',
        description: error.message || 'No se pudieron cargar los clientes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setFilteredCustomers([]);
      setCustomerDropdown(true); // Mostrar dropdown con mensaje de error
    } finally {
      setIsSearchingCustomers(false);
    }
  };

  // Función para búsqueda de servicios por matrícula en el backend
  const searchServicesInBackend = async (query) => {
    if (!query.trim()) {
      setFilteredProducts([]);
      setProductDropdown(false);
      setHasSearchedServices(false);
      setHasSearchedProducts(false);
      return;
    }

    try {
      setIsSearchingServices(true);
      setHasSearchedServices(true);
      // Buscar servicios de vehículos por matrícula
      const response = await getData(
        `/vehicleServices?page=1&limit=20&data=${encodeURIComponent(query)}&pendingInvoice=true`,
      );
      const vehicleServices = response?.payload?.vehicleServices || [];

      const formattedServices = vehicleServices
        .filter((vehicleService) => vehicleService.vehicle?.plate)
        .map((vehicleService) => {
          const serviceDate = new Date(vehicleService.date).toLocaleDateString(
            'es-UY',
            {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            },
          );
          const summary = summarizeVehicleServiceForInvoice(vehicleService);

          return {
            id: `service-${vehicleService.id}`,
            name: `${summary.serviceName} - ${formatVehicleBrandModel(vehicleService.vehicle)}`,
            type: 'service',
            plate: vehicleService.vehicle?.plate,
            vehicleServiceId: vehicleService.id,
            description: vehicleService.description,
            serviceDate,
            rawDate: vehicleService.date,
            laborCost: summary.laborCost,
            products: summary.products,
            productsTotal: summary.productsTotal,
            totalCost: summary.totalCost,
            hasLabor: summary.hasLabor,
            hasProducts: summary.hasProducts,
          };
        });

      setFilteredProducts(formattedServices);
      setProductDropdown(true);
    } catch (error) {
      toast({
        title: 'Error al buscar servicios',
        description: error.message || 'No se pudieron cargar los servicios',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setFilteredProducts([]);
      setProductDropdown(true);
    } finally {
      setIsSearchingServices(false);
    }
  };

  // Función para búsqueda de productos en el backend
  const searchProductsInBackend = async (query) => {
    if (!query.trim()) {
      setFilteredProducts([]);
      setProductDropdown(false);
      setHasSearchedProducts(false);
      return;
    }

    try {
      setIsSearchingServices(true); // Reutilizamos el estado de loading
      setHasSearchedProducts(true);

      // Buscar productos en el backend
      const response = await getData(
        `/products?data=${encodeURIComponent(query)}`,
      );

      const backendProducts = response?.payload?.products || [];
      console.log('Productos encontrados:', backendProducts);
      // Convertir productos al formato esperado
      const formattedProducts = backendProducts.map((product) => ({
        id: product.id,
        code: product.code || '',
        name: product.name,
        unit: getUnitValue(product.unit?.name) || 'un',
        price: product.currentCost || 0,
        currencyCode: product.currency?.code || 'UYU',
        type: 'product',
      }));

      setFilteredProducts(formattedProducts);
      setProductDropdown(true);
    } catch (error) {
      toast({
        title: 'Error al buscar productos',
        description: error.message || 'No se pudieron cargar los productos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setFilteredProducts([]);
      setProductDropdown(true);
    } finally {
      setIsSearchingServices(false);
    }
  };

  // Función para convertir números con coma a punto
  const parseDecimalValue = (value) => {
    if (typeof value === 'string') {
      // Reemplazar coma por punto para permitir números decimales con coma
      const normalizedValue = value.replace(',', '.');
      const parsedValue = parseFloat(normalizedValue);
      return isNaN(parsedValue) ? 0 : parsedValue;
    }
    return parseFloat(value) || 0;
  };

  // Función para validar si un string puede ser un número válido (incluyendo estados intermedios)
  const isValidNumberInput = (value) => {
    if (typeof value !== 'string') return true;
    // Permitir valores vacíos, números, comas y puntos
    return /^[\d,.\s]*$/.test(value);
  };

  // Función para calcular correctamente subtotal, IVA y total
  const calculateItemTotals = (item) => {
    const discountRate = Math.min(100, Math.max(0, parseDec(item.descuento_porcentaje || 0))) / 100;
    const precioBruto = parseDec(item.cantidad) * parseDec(item.precio);
    const precioTotal = precioBruto * (1 - discountRate);
    const ivaRate = (item.iva_porcentaje || 22) / 100;

    if (item.ivaIncluded) {
      item.subtotal = precioTotal / (1 + ivaRate);
      item.iva_monto = precioTotal - item.subtotal;
      item.total = precioTotal;
    } else {
      item.subtotal = precioTotal;
      item.iva_monto = precioTotal * ivaRate;
      item.total = item.subtotal + item.iva_monto;
    }
    return item;
  };

  const updateItem = (itemId, field, value) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item };
          
          // Para campos numéricos, manejar tanto el valor de entrada como el valor numérico
          if (field === 'cantidad' || field === 'precio') {
            if (!isValidNumberInput(value)) {
              return item;
            }
            updatedItem[`${field}_input`] = value;
            updatedItem[field] = parseDec(value);
          } else if (field === 'descuento_porcentaje') {
            updatedItem[field] = parseDec(value);
          } else {
            updatedItem[field] = value;
          }
          
          if (field === 'cantidad' || field === 'precio' || field === 'ivaIncluded' || field === 'descuento_porcentaje') {
            calculateItemTotals(updatedItem);
          }

          return updatedItem;
        }
        return item;
      })
    }));
  };


  const handleCustomerKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchCustomersInBackend(customerSearch);
    }
  };

  const handleProductKeyPress = (e) => {
    if (e.key === 'Enter') {
      // Si parece ser una matrícula (formato típico: ABC123, ABC1234, etc.)
      const isPlateFormat = /^[A-Za-z]{2,3}\d{3,4}$/.test(productSearch.trim());

      if (isPlateFormat) {
        // Reset product search state when searching services
        setHasSearchedProducts(false);
        searchServicesInBackend(productSearch);
      } else {
        // Reset services search state when searching products
        setHasSearchedServices(false);
        searchProductsInBackend(productSearch);
      }
    }
  };

  // Funciones para manejar el modal de agregar cliente
  const openAddCustomerModal = () => {
    setIsAddCustomerModalOpen(true);
  };

  const closeAddCustomerModal = () => {
    setIsAddCustomerModalOpen(false);
  };

  const handleCustomerCreated = (newCustomer) => {
    applyCustomerToForm(newCustomer);

    toast({
      title: 'Cliente agregado exitosamente',
      description: `${newCustomer.name} ha sido seleccionado automáticamente`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Funciones para productos manuales
  const addManualProduct = () => {
    setShowManualProductForm(true);
  };

  const addManualProductToForm = (productData) => {
    const manualProduct = {
      id: Date.now(), // ID temporal
      code: productData.code || '',
      name: productData.name,
      unit: productData.unit,
      price: parseFloat(productData.price),
      type: 'manual',
    };

    addItem(manualProduct);
    setShowManualProductForm(false);
    setProductSearch('');

    toast({
      title: 'Producto agregado manualmente',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleChange = (path, value) => {
    const keys = path.split('.');
    setForm((prev) => {
      const updatedForm = { ...prev };
      let ref = updatedForm;
      keys.forEach((key, idx) => {
        if (idx === keys.length - 1) ref[key] = value;
        else ref = ref[key];
      });
      return updatedForm;
    });
  };

  const handleTipoComprobanteChange = (value) => {
    const tipo = parseInt(value, 10);
    setForm((prev) => ({
      ...prev,
      tipo_comprobante: tipo,
      cliente: {
        ...prev.cliente,
        tipo_doc: getDefaultDocTypeForInvoiceType(tipo),
      },
    }));
  };

  const applyCustomerToForm = (customer) => {
    const document =
      customer.document ||
      customer.documentNumber ||
      customer.nro_doc ||
      customer.nro_documento ||
      '';
    const tipoDoc =
      customer.documentType != null
        ? Number(customer.documentType)
        : getDefaultDocTypeForInvoiceType(form.tipo_comprobante);

    setCustomerSearch(customer.name || '');
    handleChange('cliente.tipo_doc', tipoDoc);
    handleChange('cliente.nro_doc', document);
    handleChange('cliente.denominacion', customer.name || '');
    handleChange('cliente.direccion', customer.address || '');
    handleChange('cliente.ciudad', customer.city || '');
    handleChange('cliente.departamento', customer.state || '');
    handleChange('cliente.cod_pais_doc', customer.documentCountry || 'UY');
    setCustomerDropdown(false);
    setHasSearchedCustomers(false);
  };

  const addItem = async (product) => {
    console.log('Agregando producto:', product);
    if (product.type === 'service' && product.vehicleServiceId) {
      try {
        const response = await getData(
          `/vehicleServices/${product.vehicleServiceId}`,
        );
        const vehicleService = response?.payload?.vehicleService;

        if (vehicleService) {
          const rate = Number(form.tipo_cambio) || bcuRate || 0;
          const rawLines = buildInvoiceLinesFromVehicleService(vehicleService, {
            getUnitValue,
            calculateItemTotals,
          });
          const invoiceLines = rawLines.map((line, index) => {
            const converted = applyExchangeToLine(line, rate);
            calculateItemTotals(converted);
            return {
              ...converted,
              id: `${line.id}-${Date.now()}-${index}`,
            };
          });

          if (!invoiceLines.length) {
            toast({
              title: 'Servicio sin ítems facturables',
              description:
                'El servicio no tiene mano de obra ni productos utilizados registrados.',
              status: 'warning',
              duration: 4000,
              isClosable: true,
            });
            return;
          }

          setForm((prev) => ({
            ...prev,
            items: [...prev.items, ...invoiceLines],
          }));

          toast({
            title: 'Servicio agregado',
            description:
              invoiceLines.length > 1
                ? `Se agregaron ${invoiceLines.length} líneas (mano de obra y productos)`
                : 'Se agregó la línea del servicio',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
      } catch (error) {
        toast({
          title: 'Error al cargar el servicio',
          description:
            error?.message || 'No se pudieron obtener los detalles del servicio',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } else {
      // Para productos normales o manuales
      const quantity =
        product.type === 'used-product' || product.type === 'manual-product'
          ? product.quantity || 1
          : 1;

      const rate = Number(form.tipo_cambio) || bcuRate || 0;
      const productCurrency = product.currencyCode || 'UYU';
      let unitPrice = Number(product.price) || 0;
      if (form.moneda === 'UYU' && isUsdCurrency(productCurrency)) {
        unitPrice = toUyuAmount(unitPrice, productCurrency, rate) ?? unitPrice;
      } else if (form.moneda === 'USD' && !isUsdCurrency(productCurrency) && rate > 0) {
        unitPrice = unitPrice / rate;
      }

      const newItem = {
        id: product.id,
        concepto: product.name,
        unidad: getUnitValue(product.unit) || 'un',
        cantidad: quantity,
        cantidad_input: quantity.toString(),
        precio: unitPrice,
        precio_input: unitPrice.toString(),
        indicador_facturacion: 3,
        ivaIncluded: false, // Por defecto IVA no incluido
        iva_porcentaje: 22, // IVA estándar
      };

      // Agregar código si existe (campo cod_item para DGI)
      if (product.code) {
        newItem.cod_item = product.code;
      }

      // Calcular campos derivados
      calculateItemTotals(newItem);

      setForm((prev) => ({
        ...prev,
        items: [...prev.items, newItem],
      }));
    }
  };

  const removeItem = (itemId) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };


  const buildPayload = (formData) => {
    const billableItems = formData.items.filter((i) => !i.es_redondeo);
    // Redondeo a peso solo aplica en UYU
    const rounding =
      formData.moneda === 'UYU' ? buildRoundingLineItem(billableItems) : null;
    const rawItems = rounding ? [...billableItems, rounding] : billableItems;

    const adendaParts = [];
    if (formData.numero_orden_compra?.trim()) {
      adendaParts.push(`Orden de compra: ${formData.numero_orden_compra.trim()}`);
    }
    if (formData.adenda?.texto?.trim()) {
      adendaParts.push(formData.adenda.texto.trim());
    }

    const cliente = formData.cliente || {};
    const departamento =
      cliente.departamento || cliente.dep_prov_estado || cliente.state || '';

    // Solo campos que acepta la API FEU (sin campos de UI locales)
    const items = rawItems.map((item) => {
      const clean = {
        cantidad: Number(item.cantidad) || 0,
        concepto: String(item.concepto || '').slice(0, 80),
        precio: Number(item.precio) || 0,
        unidad: item.unidad || 'N/A',
        indicador_facturacion: Number(item.indicador_facturacion) || 3,
      };
      if (item.cod_item) clean.cod_item = String(item.cod_item);
      if (item.descuento_porcentaje) {
        clean.descuento = Number(item.descuento_porcentaje);
      }
      return clean;
    });

    const payload = {
      tipo_comprobante: formData.tipo_comprobante,
      moneda: formData.moneda,
      fecha_comprobante: formData.fecha_comprobante,
      fecha_vencimiento: formData.due_date || formData.fecha_comprobante,
      // Se mantiene due_date para guardar localmente en el backend
      due_date: formData.due_date || formData.fecha_comprobante,
      forma_pago: formData.forma_pago,
      cliente: {
        tipo_doc: Number(cliente.tipo_doc) || 2,
        cod_pais_doc: cliente.cod_pais_doc || 'UY',
        nro_doc: String(cliente.nro_doc || '').replace(/\D/g, ''),
        denominacion: cliente.denominacion || '',
        direccion: cliente.direccion || 'N/D',
        ciudad: cliente.ciudad || 'N/D',
        pais: cliente.pais || 'URUGUAY',
        ...(departamento
          ? { dep_prov_estado: String(departamento).slice(0, 30) }
          : {}),
      },
      items,
      numero_orden_compra: formData.numero_orden_compra || '',
    };

    // FEU rechaza arrays vacíos: solo enviar si hay al menos 1 elemento
    if (
      Array.isArray(formData.descuentos_recargos) &&
      formData.descuentos_recargos.length > 0
    ) {
      payload.descuentos_recargos = formData.descuentos_recargos;
    }

    const adendaTexto = buildInvoiceAdendaText(adendaParts);
    payload.adenda = { texto: adendaTexto };

    // Si algún ítem tiene IVA incluido, avisar a FEU
    if (rawItems.some((i) => i.ivaIncluded)) {
      payload.cod_montos_brutos = 1;
    }

    if (formData.moneda === 'USD') {
      payload.tipo_cambio = Number(formData.tipo_cambio) || undefined;
    }

    return payload;
  };

  const displayItems = useMemo(() => {
    const billable = form.items.filter((i) => !i.es_redondeo);
    if (form.moneda !== 'UYU') return billable;
    const rounding = buildRoundingLineItem(billable);
    return rounding ? [...billable, rounding] : billable;
  }, [form.items, form.moneda]);

  const totals = useMemo(() => {
    const subtotal = displayItems.reduce((s, i) => s + (i.subtotal || 0), 0);
    const iva = displayItems.reduce((s, i) => s + (i.iva_monto || 0), 0);
    const total = sumItemsTotal(displayItems);
    const rate = Number(form.tipo_cambio) || 0;
    return {
      subtotal,
      iva,
      total,
      totalUyu: form.moneda === 'USD' && rate > 0 ? total * rate : total,
    };
  }, [displayItems, form.moneda, form.tipo_cambio]);

  const moneyLabel = form.moneda === 'USD' ? 'USD' : 'UYU';
  const formatMoney = (amount) => `${moneyLabel} ${formatNumber(amount)}`;

  const handleGeneratePreviewPdf = async () => {
    if (form.moneda === 'USD' && !(Number(form.tipo_cambio) > 0)) {
      toast({
        title: 'Falta tipo de cambio',
        description: 'Espere a que se cargue la cotización del BCU',
        status: 'warning',
        duration: 3500,
      });
      return;
    }
    const clientError = validateInvoiceClient(form);
    if (clientError) {
      toast({
        title: 'Datos del receptor incompletos',
        description: clientError,
        status: 'warning',
        duration: 4500,
        isClosable: true,
      });
      return;
    }
    if (!displayItems.filter((i) => !i.es_redondeo).length) {
      toast({
        title: 'Sin ítems',
        description: 'Agregá al menos un producto o servicio.',
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    setGeneratingPreviewPdf(true);
    try {
      const payload = {
        ...buildPayload(form),
        preview: {
          tipo_label: getInvoiceTypeLabel(form.tipo_comprobante),
          items: displayItems.map((item) => ({
            concepto: item.concepto,
            cantidad: item.cantidad,
            unidad: item.unidad,
            precio: item.precio,
            descuento_porcentaje: item.descuento_porcentaje || 0,
            subtotal: item.subtotal || 0,
            iva_monto: item.iva_monto || 0,
            total: item.total || 0,
            es_redondeo: Boolean(item.es_redondeo),
          })),
          totals,
        },
      };

      const response = await api.post('/invoices/preview-pdf', payload, {
        responseType: 'arraybuffer',
        skipGlobalLoader: true,
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      if (!blob.size) {
        throw new Error('El PDF generado está vacío');
      }

      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 15000);
    } catch (error) {
      toast({
        title: 'No se pudo generar el PDF',
        description: error?.message || 'Error al generar la previsualización',
        status: 'error',
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setGeneratingPreviewPdf(false);
    }
  };

  const cardBg = useColorModeValue('white', 'gray.800');

  // Componente inline para formulario de producto manual
  const ManualProductForm = ({ onAdd, onCancel }) => {
    const [manualProduct, setManualProduct] = useState({
      code: '',
      name: '',
      unit: 'un',
      price: '',
    });

    const handleManualProductChange = (field, value) => {
      setManualProduct((prev) => ({ ...prev, [field]: value }));
    };

    const handleAddManualProduct = () => {
      if (!manualProduct.name.trim() || !manualProduct.price) {
        toast({
          title: 'Error',
          description: 'Nombre y precio son obligatorios',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Usar parseDecimalValue para manejar comas en el precio
      const productWithParsedPrice = {
        ...manualProduct,
        price: parseDecimalValue(manualProduct.price)
      };

      onAdd(productWithParsedPrice);
      setManualProduct({ code: '', name: '', unit: 'un', price: '' });
    };

    return (
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
        <FormControl>
          <FormLabel fontSize="sm">Código</FormLabel>
          <Input
            size="sm"
            value={manualProduct.code}
            onChange={(e) => handleManualProductChange('code', e.target.value)}
            placeholder="Código del producto"
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Nombre del producto</FormLabel>
          <Input
            size="sm"
            value={manualProduct.name}
            onChange={(e) => handleManualProductChange('name', e.target.value)}
            placeholder="Ej: Filtro de aceite"
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Unidad</FormLabel>
          <Select
            size="sm"
            value={manualProduct.unit}
            onChange={(e) => handleManualProductChange('unit', e.target.value)}
          >
            <option value="un">UNIDAD</option>
            <option value="kg">KILO</option>
            <option value="lt">LITRO</option>
            <option value="m">METRO</option>
            <option value="h">HORA</option>
            <option value="min">MINUTO</option>
          </Select>
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Precio</FormLabel>
          <Input
            size="sm"
            type="text"
            value={manualProduct.price}
            onChange={(e) => handleManualProductChange('price', e.target.value)}
            placeholder="0,00"
          />
        </FormControl>
        <HStack mt={2} gridColumn={{ base: 'span 1', md: 'span 4' }}>
          <Button
            size="sm"
            colorScheme="gray"
            onClick={handleAddManualProduct}
          >
            Agregar
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
        </HStack>
      </SimpleGrid>
    );
  };

  return (
    <Box p={{ base: 3, md: 4 }} borderWidth={1} borderRadius="md" bg={cardBg}>
      <VStack spacing={4} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Tipo de comprobante</FormLabel>
            <Select
              size="sm"
              value={form.tipo_comprobante}
              onChange={(e) => handleTipoComprobanteChange(e.target.value)}
            >
              {INVOICE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Text fontSize="xs" color="gray.500" mt={1}>
              {form.tipo_comprobante === INVOICE_TYPE_E_FACTURA
                ? 'Para empresas o personas con RUT (12 dígitos).'
                : 'Para consumidor final (C.I. u otro documento).'}
            </Text>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Moneda</FormLabel>
            <Select size="sm"
              value={form.moneda}
              onChange={(e) => handleChange('moneda', e.target.value)}
            >
              <option value="UYU">UYU</option>
              <option value="USD">USD</option>
            </Select>
          </FormControl>
          {form.moneda === 'USD' && (
            <FormControl>
              <FormLabel fontSize="sm">Tipo de cambio (BCU)</FormLabel>
              <Input
                size="sm"
                value={
                  loadingExchangeRate
                    ? 'Consultando BCU…'
                    : form.tipo_cambio
                      ? Number(form.tipo_cambio).toFixed(3)
                      : ''
                }
                isReadOnly
                bg="gray.50"
              />
              {exchangeRateInfo?.date && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Cotización venta {exchangeRateInfo.date} · Fuente BCU
                </Text>
              )}
            </FormControl>
          )}
          <FormControl>
            <FormLabel fontSize="sm">Forma de pago</FormLabel>
            <Select size="sm"
              value={form.forma_pago}
              onChange={(e) =>
                handleChange('forma_pago', parseInt(e.target.value))
              }
            >
              <option value={1}>Contado</option>
              <option value={2}>Crédito</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Fecha *</FormLabel>
            <Input size="sm" type="date"
              value={form.fecha_comprobante}
              onChange={(e) =>
                handleChange('fecha_comprobante', e.target.value)
              }
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Fecha de vencimiento</FormLabel>
            <Input size="sm" type="date"
              value={form.due_date}
              onChange={(e) =>
                handleChange('due_date', e.target.value)
              }
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Nº orden de compra</FormLabel>
            <Input
              size="sm"
              value={form.numero_orden_compra}
              onChange={(e) => handleChange('numero_orden_compra', e.target.value)}
              placeholder="Identificador de compra"
            />
          </FormControl>
        </SimpleGrid>

        <FormControl>
          <FormLabel fontSize="sm">Cliente</FormLabel>
          <HStack align="start">
            <Box flex="1" position="relative">
              <InputGroup>
                <Input
                  placeholder={
                    isSearchingCustomers
                      ? 'Buscando...'
                      : 'Buscar por nombre o RUT… (Enter)'
                  }
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  onKeyPress={handleCustomerKeyPress}
                  isDisabled={isSearchingCustomers}
                  onBlur={() =>
                    setTimeout(() => {
                      setCustomerDropdown(false);
                      setHasSearchedCustomers(false);
                    }, 150)
                  }
                />
                <InputRightElement pointerEvents="none">
                  <ChevronDownIcon />
                </InputRightElement>
              </InputGroup>

              {customerDropdown &&
                (filteredCustomers.length > 0 ||
                  isSearchingCustomers ||
                  (hasSearchedCustomers && filteredCustomers.length === 0)) && (
                  <List
                    bg="white"
                    border="1px solid #ccc"
                    mt={1}
                    borderRadius="md"
                    maxHeight="150px"
                    overflowY="auto"
                    position="absolute"
                    zIndex={10}
                    w="100%"
                  >
                    {filteredCustomers.length ? (
                      filteredCustomers.map((c) => (
                        <ListItem
                          key={c.id}
                          px={4}
                          py={2}
                          _hover={{ bg: 'gray.100' }}
                          cursor="pointer"
                          onMouseDown={() => applyCustomerToForm(c)}
                        >
                          <Box>
                            <Text fontWeight="medium">{c.name}</Text>
                            {(c.document || c.documentNumber || c.nro_doc || c.nro_documento) && (
                              <Text fontSize="sm" color="gray.600">
                                RUT: {c.document || c.documentNumber || c.nro_doc || c.nro_documento}
                              </Text>
                            )}
                          </Box>
                        </ListItem>
                      ))
                    ) : isSearchingCustomers ? (
                      <ListItem px={4} py={2} color="blue.500">
                        Buscando clientes...
                      </ListItem>
                    ) : (
                      <ListItem px={4} py={2} color="gray.500">
                        No se encontraron clientes
                      </ListItem>
                    )}
                  </List>
                )}
            </Box>

            <Button onClick={openAddCustomerModal} minW="40px">
              <AddIcon />
            </Button>
          </HStack>
        </FormControl>

        {/* Documento del cliente seleccionado (solo lectura) */}
        {form.cliente.nro_doc ? (
          <Box
            px={3}
            py={2}
            bg="gray.50"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="md"
            maxW={{ base: '100%', md: '100%' }}
          >
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              <FormControl maxW={{ base: '100%', md: '200px' }}>
                <FormLabel fontSize="xs" color="gray.500" mb={1}>
                  Tipo de documento
                </FormLabel>
                <Select
                  size="sm"
                  bg="white"
                  value={form.cliente.tipo_doc}
                  onChange={(e) =>
                    handleChange('cliente.tipo_doc', parseInt(e.target.value, 10))
                  }
                >
                  {DOC_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Text fontSize="xs" color="gray.500" mb={0.5}>
                  Documento
                </Text>
                <Text fontSize="sm" fontWeight="medium" color="gray.800">
                  {form.cliente.nro_doc}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500" mb={0.5}>
                  Denominación
                </Text>
                <Text fontSize="sm" fontWeight="medium" color="gray.800">
                  {form.cliente.denominacion || '—'}
                </Text>
              </Box>
            </SimpleGrid>
          </Box>
        ) : (
          <FormControl maxW={{ base: '100%', md: '240px' }}>
            <FormLabel fontSize="sm">Tipo de documento del receptor</FormLabel>
            <Select
              size="sm"
              value={form.cliente.tipo_doc}
              onChange={(e) =>
                handleChange('cliente.tipo_doc', parseInt(e.target.value, 10))
              }
            >
              {DOC_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
        )}

        <Divider />
        <Heading size="md">Productos</Heading>

        <FormControl>
          <FormLabel>
            Buscar producto por nombre o servicio por dato del vehículo
          </FormLabel>
          <HStack align="start">
            <Box flex="1" position="relative">
              <InputGroup>
                <Input
                  placeholder={
                    isSearchingServices
                      ? 'Buscando...'
                      : 'Nombre de producto o matrícula (ABC123) — servicios finalizados sin factura'
                  }
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  onKeyPress={handleProductKeyPress}
                  isDisabled={isSearchingServices}
                  onBlur={() =>
                    setTimeout(() => {
                      setProductDropdown(false);
                      setHasSearchedServices(false);
                      setHasSearchedProducts(false);
                    }, 150)
                  }
                />
                <InputRightElement pointerEvents="none">
                  <ChevronDownIcon />
                </InputRightElement>
              </InputGroup>
              {productDropdown &&
                (filteredProducts.length > 0 ||
                  isSearchingServices ||
                  (hasSearchedServices && filteredProducts.length === 0) ||
                  (hasSearchedProducts && filteredProducts.length === 0)) && (
                  <List
                    bg="white"
                    border="1px solid #ccc"
                    mt={1}
                    borderRadius="md"
                    maxHeight="150px"
                    overflowY="auto"
                    position="absolute"
                    zIndex={10}
                    w="100%"
                  >
                    {filteredProducts.length ? (
                      filteredProducts.map((p) => (
                        <ListItem
                          key={p.id}
                          px={4}
                          py={2}
                          _hover={{ bg: 'gray.100' }}
                          cursor="pointer"
                          onMouseDown={() => {
                            addItem(p);
                            setProductSearch('');
                            setProductDropdown(false);
                            setHasSearchedServices(false);
                            setHasSearchedProducts(false);
                          }}
                        >
                          <Box>
                            <Text fontWeight="medium" fontSize="sm">
                              {p.code && p.type === 'product' ? `${p.name} (${p.code})` : p.name}
                            </Text>
                            {p.plate && (
                              <Text fontSize="xs" color="blue.600">
                                Matrícula: {p.plate}
                              </Text>
                            )}
                            {p.serviceDate && (
                              <Text fontSize="xs" color="gray.500">
                                📅 Fecha: {p.serviceDate}
                              </Text>
                            )}
                            {p.type === 'service' ? (
                              <Box>
                                {p.hasLabor ? (
                                  <Text fontSize="xs" color="green.600">
                                    Mano de obra:{' '}
                                    {formatMoneyDual(
                                      p.laborCost,
                                      form.moneda,
                                      bcuRate,
                                    )}
                                  </Text>
                                ) : (
                                  <Text fontSize="xs" color="gray.500">
                                    Sin mano de obra registrada
                                  </Text>
                                )}
                                {p.hasProducts ? (
                                  <Box mt={1}>
                                    {p.products.map((prod) => (
                                      <Text
                                        key={`${p.id}-${prod.name}-${prod.quantity}`}
                                        fontSize="xs"
                                        color="gray.600"
                                      >
                                        · {prod.name} ({prod.quantity}{' '}
                                        {getUnitDisplay(prod.unit)}):{' '}
                                        {formatMoneyDual(
                                          prod.total,
                                          form.moneda,
                                          bcuRate,
                                        )}
                                      </Text>
                                    ))}
                                    <Text
                                      fontSize="xs"
                                      color="orange.600"
                                      mt={1}
                                    >
                                      Productos:{' '}
                                      {formatMoneyDual(
                                        p.productsTotal,
                                        form.moneda,
                                        bcuRate,
                                      )}
                                    </Text>
                                  </Box>
                                ) : (
                                  <Text fontSize="xs" color="gray.500">
                                    Sin productos utilizados
                                  </Text>
                                )}
                                <Text
                                  fontSize="xs"
                                  color="blue.600"
                                  fontWeight="medium"
                                  mt={1}
                                >
                                  Total estimado:{' '}
                                  {formatMoneyDual(
                                    p.totalCost,
                                    form.moneda,
                                    bcuRate,
                                  )}
                                </Text>
                              </Box>
                            ) : (
                              <Text fontSize="xs" color="green.600">
                                Precio:{' '}
                                {formatMoneyDual(
                                  p.price,
                                  p.currencyCode || 'UYU',
                                  bcuRate,
                                )}
                              </Text>
                            )}
                          </Box>
                        </ListItem>
                      ))
                    ) : isSearchingServices ? (
                      <ListItem px={4} py={2} color="blue.500">
                        Buscando servicios...
                      </ListItem>
                    ) : (
                      <ListItem px={4} py={2} color="gray.500">
                        No se encontraron productos o servicios
                      </ListItem>
                    )}
                  </List>
                )}
            </Box>
            <Button onClick={addManualProduct} variant="adminPrimary" minW="120px">
              + Manual
            </Button>
          </HStack>
        </FormControl>

        {/* Formulario para agregar producto manual */}
        {showManualProductForm && (
          <Box
            p={4}
            bg="gray.50"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
          >
            <VStack spacing={3} align="stretch">
              <Heading size="sm" color="green.700">
                Agregar Producto Manual
              </Heading>
              <ManualProductForm
                onAdd={addManualProductToForm}
                onCancel={() => setShowManualProductForm(false)}
              />
            </VStack>
          </Box>
        )}

        {form.items.length > 0 && (
          <Box overflowX="auto">
            <SimpleGrid columns={8} spacing={2} minChildWidth="90px" mb={2}>
              <Text fontWeight="medium" fontSize="xs" color="secondaryGray.600">Concepto</Text>
              <Text fontWeight="medium" fontSize="xs" color="secondaryGray.600">Unidad</Text>
              <Text fontWeight="medium" fontSize="xs" color="secondaryGray.600">Cant.</Text>
              <Text fontWeight="medium" fontSize="xs" color="secondaryGray.600">Precio</Text>
              <Text fontWeight="medium" fontSize="xs" color="secondaryGray.600">Desc.%</Text>
              <Text fontWeight="medium" fontSize="xs" color="secondaryGray.600">Total</Text>
              <Text fontWeight="medium" fontSize="xs" color="secondaryGray.600">IVA Inc.</Text>
              <Text fontWeight="medium" fontSize="xs" color="secondaryGray.600"></Text>
            </SimpleGrid>

            {form.items.filter((i) => !i.es_redondeo).map((item, index) => (
              <SimpleGrid key={item.id} columns={8} spacing={2} minChildWidth="90px" mb={2}>
                <Text fontSize="xs" color="secondaryGray.900" noOfLines={2}>{item.concepto}</Text>
                <Text fontSize="xs" color="secondaryGray.900">{getUnitDisplay(item.unidad)}</Text>
                <Input size="sm" type="text" value={item.cantidad_input ?? item.cantidad} onChange={(e) => updateItem(item.id, 'cantidad', e.target.value)} />
                <Input size="sm" type="text" value={item.precio_input ?? item.precio} onChange={(e) => updateItem(item.id, 'precio', e.target.value)} />
                <Input size="sm" type="text" value={item.descuento_porcentaje ?? ''} placeholder="0" onChange={(e) => updateItem(item.id, 'descuento_porcentaje', e.target.value)} />
                <Text fontSize="xs" fontWeight="medium">${formatNumber(item.total || 0)}</Text>
                <Checkbox
                  size="sm"
                  isChecked={item.ivaIncluded || false}
                  onChange={(e) => {
                    const updatedItems = [...form.items];
                    updatedItems[index].ivaIncluded = e.target.checked;
                    // Recalcular totales cuando cambia el checkbox
                    calculateItemTotals(updatedItems[index]);
                    setForm(prev => ({ ...prev, items: updatedItems }));
                  }}
                />
                <IconButton
                  icon={<DeleteIcon />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => removeItem(item.id)}
                />
              </SimpleGrid>
            ))}
          </Box>
        )}

        <Flex
          gap={4}
          align="flex-start"
          flexDirection={{ base: 'column', md: 'row' }}
          w="100%"
        >
          <FormControl flex={1} maxW={{ md: '50%' }} w="100%">
            <FormLabel>Adenda adicional</FormLabel>
            <Textarea
              size="sm"
              rows={3}
              value={form.adenda.texto}
              onChange={(e) => handleChange('adenda.texto', e.target.value)}
              placeholder="Texto opcional (orden, observaciones, etc.)"
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              Siempre se incluye: {INVOICE_BANK_ADENDA}
            </Text>
          </FormControl>

          <Box
            flex={1}
            w="100%"
            borderTop={{ base: '1px solid', md: 'none' }}
            borderColor="gray.200"
            pt={{ base: 3, md: 0 }}
            ml={{ md: 'auto' }}
            maxW={{ md: '280px' }}
          >
            <SimpleGrid columns={2} spacing={2} fontSize="sm" textAlign="right">
              <Box textAlign="left">Subtotal:</Box>
              <Box>{formatMoney(totals.subtotal)}</Box>
              <Box textAlign="left">IVA:</Box>
              <Box>{formatMoney(totals.iva)}</Box>
              {form.moneda === 'USD' && (
                <>
                  <Box textAlign="left">Tipo de cambio:</Box>
                  <Box>
                    {loadingExchangeRate
                      ? '…'
                      : Number(form.tipo_cambio || 0).toFixed(3)}
                  </Box>
                </>
              )}
              <Box textAlign="left">
                <strong>Total:</strong>
              </Box>
              <Box>
                <strong>{formatMoney(totals.total)}</strong>
              </Box>
              {form.moneda === 'USD' && Number(form.tipo_cambio) > 0 && (
                <>
                  <Box textAlign="left" color="gray.600">
                    Equiv. UYU:
                  </Box>
                  <Box color="gray.600">
                    UYU {formatNumber(totals.totalUyu)}
                  </Box>
                </>
              )}
            </SimpleGrid>
            {displayItems.some((i) => i.es_redondeo) && (
              <Text fontSize="xs" color="gray.500" mt={2} textAlign="right">
                Incluye línea de redondeo automático (producto no facturable).
              </Text>
            )}
          </Box>
        </Flex>

        <HStack justify="flex-end" spacing={2} flexWrap="wrap">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const name = window.prompt('Nombre de la plantilla');
              if (!name?.trim()) return;
              saveInvoiceTemplate({
                name: name.trim(),
                payload: buildPayload(form),
              });
              toast({ title: 'Plantilla guardada', status: 'success', duration: 2500 });
            }}
          >
            Guardar plantilla
          </Button>
          <Button size="sm" variant="outline" onClick={onPreviewOpen}>
            Previsualizar
          </Button>
          <Button
            size="sm"
            colorScheme="gray"
            isDisabled={form.moneda === 'USD' && !(Number(form.tipo_cambio) > 0)}
            onClick={() => {
              if (form.moneda === 'USD' && !(Number(form.tipo_cambio) > 0)) {
                toast({
                  title: 'Falta tipo de cambio',
                  description: 'Espere a que se cargue la cotización del BCU',
                  status: 'warning',
                  duration: 3500,
                });
                return;
              }
              const clientError = validateInvoiceClient(form);
              if (clientError) {
                toast({
                  title: 'Datos del receptor incompletos',
                  description: clientError,
                  status: 'warning',
                  duration: 4500,
                  isClosable: true,
                });
                return;
              }
              if (!form.items.filter((i) => !i.es_redondeo).length) {
                toast({
                  title: 'Sin ítems',
                  description: 'Agregá al menos un producto o servicio.',
                  status: 'warning',
                  duration: 3500,
                  isClosable: true,
                });
                return;
              }
              onSubmit(buildPayload(form));
            }}
          >
            Generar factura
          </Button>
        </HStack>
      </VStack>

      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="lg" scrollBehavior="inside" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Previsualización de factura</ModalHeader>
          <ModalCloseButton />
          <ModalBody fontSize="sm">
            <Text mb={2}>
              <strong>Tipo:</strong>{' '}
              {getInvoiceTypeLabel(form.tipo_comprobante)}
            </Text>
            <Text mb={2}><strong>Cliente:</strong> {form.cliente.denominacion || '—'}</Text>
            <Text mb={2}><strong>Documento:</strong> {form.cliente.nro_doc || '—'}</Text>
            <Text mb={2}><strong>Fecha:</strong> {form.fecha_comprobante} · <strong>Venc.:</strong> {form.due_date || form.fecha_comprobante}</Text>
            <Text mb={2}>
              <strong>Moneda:</strong> {form.moneda}
              {form.moneda === 'USD' && Number(form.tipo_cambio) > 0
                ? ` · TC BCU ${Number(form.tipo_cambio).toFixed(3)}`
                : ''}
            </Text>
            {form.numero_orden_compra && <Text mb={2}><strong>Orden compra:</strong> {form.numero_orden_compra}</Text>}
            <Divider my={3} />
            {displayItems.map((item) => (
              <Box key={item.id} py={1} borderBottomWidth="1px">
                <Text>{item.concepto} {item.es_redondeo ? '(redondeo)' : ''}</Text>
                <Text fontSize="xs" color="gray.600">
                  {item.cantidad} x {formatMoney(item.precio)} · Desc {item.descuento_porcentaje || 0}% · Total {formatMoney(item.total || 0)}
                </Text>
              </Box>
            ))}
            <Text mt={3} fontWeight="bold" textAlign="right">
              Total: {formatMoney(totals.total)}
            </Text>
            {form.moneda === 'USD' && Number(form.tipo_cambio) > 0 && (
              <Text fontSize="sm" color="gray.600" textAlign="right">
                Equiv. UYU {formatNumber(totals.totalUyu)}
              </Text>
            )}
            <Text mt={3} fontSize="xs" color="gray.500">
              El PDF de borrador no se envía a DGI y no tiene validez fiscal.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              colorScheme="gray"
              mr={2}
              isLoading={generatingPreviewPdf}
              loadingText="Generando…"
              onClick={handleGeneratePreviewPdf}
            >
              Ver PDF borrador
            </Button>
            <Button size="sm" onClick={onPreviewClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal para agregar nuevo cliente */}
      <AddCustomerModal
        isOpen={isAddCustomerModalOpen}
        onClose={closeAddCustomerModal}
        onCustomerCreated={handleCustomerCreated}
      />
    </Box>
  );
}
